import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, Typography, IconButton,
    List, ListItem, ListItemText, ListItemAvatar, Avatar, ListItemButton,
    Divider, ListSubheader, Box, Stack, useTheme
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';
import { splitPlayersForPosition, getInitials, playerLabel, POS_MAPPING } from './lineupUtils';

const PlayerSelectModal = ({ selectedPosForModal, setSelectedPosForModal, handlePlayerSelect, availablePlayers, activeStats }) => {
    const theme = useTheme();
    if (!selectedPosForModal) return null;

    const { preferred, others } = splitPlayersForPosition(
        availablePlayers, selectedPosForModal.pos_name, activeStats
    );

    return (
        <Dialog
            open={!!selectedPosForModal}
            onClose={() => setSelectedPosForModal(null)}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 6,
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 25px 50px rgba(0,0,0,0.8)'
                }
            }}
        >
            <DialogTitle sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 900, color: 'primary.main', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                        Position besetzen
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900 }}>
                        {selectedPosForModal?.pos_name}
                    </Typography>
                </Box>
                <IconButton onClick={() => setSelectedPosForModal(null)} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
                    <CloseRoundedIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, maxHeight: '70vh' }}>
                <List sx={{ pt: 0 }}>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => handlePlayerSelect('')}
                            sx={{
                                py: 2,
                                px: 3,
                                bgcolor: 'rgba(211, 47, 47, 0.05)',
                                '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' }
                            }}
                        >
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'transparent', border: '1px dashed rgba(211, 47, 47, 0.5)', color: 'error.main' }}>
                                    <PersonRemoveRoundedIcon fontSize="small" />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary="Position leeren"
                                primaryTypographyProps={{ sx: { fontWeight: 900, color: 'error.main', textTransform: 'uppercase', fontSize: '0.75rem' } }}
                            />
                        </ListItemButton>
                    </ListItem>

                    {preferred.length > 0 && (
                        <ListSubheader
                            disableSticky
                            sx={{
                                bgcolor: 'transparent',
                                color: 'primary.main',
                                fontWeight: 900,
                                textTransform: 'uppercase',
                                fontSize: '1.5rem',
                                letterSpacing: '0.1em',
                                pt: 3,
                                pb: 1
                            }}>
                            ★ Empfohlen
                        </ListSubheader>
                    )}
                    {preferred.map(p => {
                        const statCount = activeStats[p.id]?.[POS_MAPPING[selectedPosForModal.pos_name]] ?? 0;
                        return (
                            <ListItem disablePadding key={p.id} sx={{ bgcolor: "#c5c3c3", mb: 0.5, boxShadow: 1, borderRadius: 1 }}>
                                <ListItemButton onClick={() => handlePlayerSelect(p.id)} sx={{ py: 1.5, px: 2 }}>
                                    <ListItemText
                                        primary={playerLabel(p)}
                                        secondary={statCount > 0 ? `${statCount} Einsätze hier` : 'Keine Einsätze hier'}
                                        primaryTypographyProps={{ sx: { fontWeight: 900, fontSize: '1rem' } }}
                                        secondaryTypographyProps={{ sx: { fontWeight: 700, fontSize: '0.7rem' } }}
                                    />
                                    {statCount > 0 && <StarRoundedIcon sx={{ color: 'primary.main', fontSize: '1.2rem', opacity: 0.5 }} />}
                                </ListItemButton>
                            </ListItem>
                        );
                    })}

                    <ListSubheader
                        disableSticky
                        sx={{
                            bgcolor: 'transparent',
                            color: 'black',
                            fontWeight: 900,
                            textTransform: 'uppercase',
                            fontSize: '1.5rem',
                            letterSpacing: '0.1em',
                            pt: 3,
                            pb: 1
                        }}>
                        Kader
                    </ListSubheader>
                    {others.map(p => {
                        const statCount = activeStats[p.id]?.[POS_MAPPING[selectedPosForModal.pos_name]] ?? 0;
                        return (
                            <ListItem disablePadding key={p.id} sx={{ bgcolor: "#c5c3c3", mb: 0.5, boxShadow: 1, borderRadius: 1 }}>
                                <ListItemButton onClick={() => handlePlayerSelect(p.id)} sx={{ py: 1.5, px: 2 }}>
                                    <ListItemText
                                        primary={playerLabel(p)}
                                        secondary={statCount > 0 ? `${statCount} Einsätze hier` : 'Keine Einsätze hier'}
                                        primaryTypographyProps={{ sx: { fontWeight: 800, fontSize: '0.95rem' } }}
                                        secondaryTypographyProps={{ sx: { fontWeight: 700, fontSize: '0.7rem' } }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </DialogContent>
        </Dialog>
    );
};

export default PlayerSelectModal;
